import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Receipt, ArrowLeft, Calendar, DollarSign, TrendingUp, FileText, Download, Minus, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import axios from "axios";



const TaxPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("yearly");
  const [expenses, setExpenses] = useState(() => {
    try { return JSON.parse(localStorage.getItem("stack_expenses") || "[]"); } catch { return []; }
  });
  const [newExpense, setNewExpense] = useState({ description: "", amount: "" });
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/wallet/transactions`, { withCredentials: true });
        setTransactions(res.data.transactions || []);
      } catch { setTransactions([]); }
      setLoading(false);
    };
    fetchData();
  }, []);

  const earnings = transactions.filter((t) => t.type === "earning");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // Monthly breakdown
  const monthlyData = monthNames.map((name, i) => {
    const monthEarnings = earnings.filter((t) => {
      const d = new Date(t.created_at);
      return d.getFullYear() === currentYear && d.getMonth() === i;
    });
    const gross = monthEarnings.reduce((s, t) => s + (t.amount || 0), 0);
    return { month: name, gross, fees: gross * 0.2, net: gross * 0.8, count: monthEarnings.length };
  });

  const totalGross = earnings.reduce((s, t) => s + (t.amount || 0), 0);
  const platformFees = totalGross * 0.2;
  const totalExpenses = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const netIncome = totalGross - platformFees;
  const taxableIncome = netIncome - totalExpenses;

  const addExpense = () => {
    if (!newExpense.description || !newExpense.amount) return;
    const updated = [...expenses, { ...newExpense, id: Date.now(), date: new Date().toISOString() }];
    setExpenses(updated);
    localStorage.setItem("stack_expenses", JSON.stringify(updated));
    setNewExpense({ description: "", amount: "" });
    setShowExpenseForm(false);
    toast.success("Expense added");
  };

  const removeExpense = (id) => {
    const updated = expenses.filter((e) => e.id !== id);
    setExpenses(updated);
    localStorage.setItem("stack_expenses", JSON.stringify(updated));
  };

  const downloadCSV = () => {
    const rows = [["Date", "Description", "Gross Amount", "Platform Fee (20%)", "Net Amount"]];
    earnings.forEach((t) => {
      rows.push([new Date(t.created_at).toLocaleDateString(), t.description || "Service earning", (t.amount || 0).toFixed(2), ((t.amount || 0) * 0.2).toFixed(2), ((t.amount || 0) * 0.8).toFixed(2)]);
    });
    if (expenses.length) {
      rows.push([]); rows.push(["EXPENSES"]); rows.push(["Date", "Description", "Amount"]);
      expenses.forEach((e) => rows.push([new Date(e.date).toLocaleDateString(), e.description, e.amount]));
    }
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `stack_tax_${currentYear}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Tax document downloaded");
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="tax-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/professional/dashboard" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Work Hub
        </Link>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-emerald-950 flex items-center gap-2"><Receipt className="w-6 h-6 text-violet-600" /> Tax Information</h1>
              <p className="text-stone-500 text-sm mt-1">Tax year {currentYear}</p>
            </div>
            <Button variant="outline" onClick={downloadCSV} className="rounded-full" data-testid="download-btn">
              <Download className="w-4 h-4 mr-1.5" /> Download CSV
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="gross-income-card">
              <DollarSign className="w-5 h-5 text-green-600 mb-1" />
              <p className="text-xl font-bold text-emerald-950">${totalGross.toFixed(2)}</p>
              <p className="text-xs text-stone-500">Gross Income</p>
            </div>
            <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="platform-fees-card">
              <TrendingUp className="w-5 h-5 text-amber-500 mb-1" />
              <p className="text-xl font-bold text-emerald-950">${platformFees.toFixed(2)}</p>
              <p className="text-xs text-stone-500">Platform Fees</p>
            </div>
            <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="expenses-card">
              <Minus className="w-5 h-5 text-red-500 mb-1" />
              <p className="text-xl font-bold text-emerald-950">${totalExpenses.toFixed(2)}</p>
              <p className="text-xs text-stone-500">Expenses</p>
            </div>
            <div className="bg-gradient-to-br from-violet-600 to-violet-700 rounded-2xl p-4 text-white" data-testid="taxable-income-card">
              <FileText className="w-5 h-5 opacity-80 mb-1" />
              <p className="text-xl font-bold">${taxableIncome.toFixed(2)}</p>
              <p className="text-xs text-violet-200">Taxable Income</p>
            </div>
          </div>

          {/* Monthly Breakdown */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-6" data-testid="monthly-breakdown">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-emerald-950">Monthly Breakdown</h3>
              <Badge className="bg-violet-100 text-violet-700"><Calendar className="w-3 h-3 mr-1" /> {currentYear}</Badge>
            </div>
            <div className="space-y-2">
              {monthlyData.filter((m) => m.gross > 0 || monthNames.indexOf(m.month) <= currentMonth).map((m, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-stone-600 w-8">{m.month}</span>
                    <div className="w-24 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${totalGross > 0 ? (m.gross / totalGross) * 100 : 0}%` }} />
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-emerald-950">${m.gross.toFixed(2)}</span>
                    <span className="text-xs text-stone-400 ml-2">({m.count} jobs)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Expense Tracking */}
          <div className="bg-white rounded-2xl border border-stone-100 p-5 mb-6" data-testid="expense-tracker">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-emerald-950">Expense Tracking</h3>
              <Button size="sm" variant="outline" onClick={() => setShowExpenseForm(!showExpenseForm)} className="rounded-full" data-testid="add-expense-btn">+ Add</Button>
            </div>
            {showExpenseForm && (
              <div className="bg-stone-50 rounded-xl p-4 mb-4 space-y-3" data-testid="expense-form">
                <Input placeholder="Description (e.g., Gas, Vehicle Maintenance)" value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} data-testid="expense-desc-input" />
                <div className="flex gap-2">
                  <Input type="number" step="0.01" placeholder="Amount" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })} data-testid="expense-amount-input" />
                  <Button onClick={addExpense} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-expense-btn">Save</Button>
                </div>
              </div>
            )}
            {expenses.length === 0 ? (
              <p className="text-stone-400 text-sm text-center py-4">No expenses tracked. Add fuel, maintenance, and other work expenses for tax deductions.</p>
            ) : (
              <div className="space-y-2">
                {expenses.map((e) => (
                  <div key={e.id} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-stone-700">{e.description}</p>
                      <p className="text-xs text-stone-400">{new Date(e.date).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-red-600">-${parseFloat(e.amount).toFixed(2)}</span>
                      <button onClick={() => removeExpense(e.id)} className="text-stone-400 hover:text-red-500"><Minus className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Disclaimer */}
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 text-sm text-violet-800" data-testid="tax-disclaimer">
            <p className="font-medium mb-1">Tax Disclaimer</p>
            <p className="text-violet-600">This summary is for informational purposes only and does not constitute tax advice. Please consult a qualified tax professional. You are responsible for reporting all income earned through Stack.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TaxPage;
